/**
 * Design: drawing deliverables and their revisions, and submittals.
 */

import { request } from './client.js';
import { pageCall, type RegisterPage } from './paging.js';

// ----------------------------------------------------------------- deliverables

export type DeliverableStatus =
  | 'planned'
  | 'in_progress'
  | 'issued'
  | 'superseded'
  | 'cancelled';

export const DISCIPLINES = [
  'architectural',
  'structural',
  'mep',
  'joinery',
  'interior',
  'other',
] as const;
export type Discipline = (typeof DISCIPLINES)[number];

export interface Revision {
  readonly id: string;
  readonly revision_code: string;
  readonly issued_at: string;
  readonly issued_by_name: string | null;
  readonly superseded_at: string | null;
  readonly is_current: boolean;
  readonly notes: string | null;
  readonly document_id: string | null;
}

export interface Deliverable {
  readonly id: string;
  readonly number: string;
  readonly title: string;
  readonly discipline: Discipline;
  readonly status: DeliverableStatus;
  readonly owner_id: string | null;
  readonly owner_name: string | null;
  readonly location_id: string | null;
  readonly location_path: string | null;
  readonly planned_issue_date: string | null;
  readonly actual_issue_date: string | null;
  readonly cancelled_reason: string | null;
  readonly current_revision: string | null;
  readonly revision_count: number;
  /** Promised date passed with nothing issued. Derived by the server on read. */
  readonly overdue: boolean;
  readonly days_late: number | null;
  /** What THIS caller may move it to. Never contains `issued` or `superseded`. */
  readonly allowed_next: readonly DeliverableStatus[];
  readonly revisions: readonly Revision[];
}

/** Every write answers with the whole register, so the screen never guesses. */
async function deliverableCall(path: string, init?: RequestInit): Promise<readonly Deliverable[]> {
  const body = await request<{ data: readonly Deliverable[] }>(path, init ?? {});
  return body.data;
}

const dwgPath = (projectId: string): string =>
  `/v1/projects/${encodeURIComponent(projectId)}/deliverables`;

/**
 * The register, which the server bounds. A subcontractor's total counts only
 * what they may see — see `api/paging.ts`.
 */
export function listDeliverables(
  projectId: string,
  limit?: number,
): Promise<RegisterPage<Deliverable>> {
  return pageCall<Deliverable>(dwgPath(projectId), limit);
}

export function createDeliverable(
  projectId: string,
  input: {
    number: string;
    title: string;
    discipline?: Discipline;
    owner_id?: string | null;
    planned_issue_date?: string | null;
    /** Issue this revision with the drawing. Blank or absent leaves it planned. */
    revision_code?: string | null;
  },
): Promise<readonly Deliverable[]> {
  return deliverableCall(dwgPath(projectId), { method: 'POST', body: JSON.stringify(input) });
}

export function updateDeliverable(
  projectId: string,
  deliverableId: string,
  input: {
    title?: string;
    discipline?: Discipline;
    owner_id?: string | null;
    planned_issue_date?: string | null;
    /** Corrects the CURRENT revision's code. Refused once it has gone out. */
    revision_code?: string;
  },
): Promise<readonly Deliverable[]> {
  return deliverableCall(`${dwgPath(projectId)}/${encodeURIComponent(deliverableId)}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/**
 * Issue a revision — the only way a drawing becomes `issued`.
 *
 * There is deliberately no call that sets the status to `issued` directly. A
 * drawing is issued because a revision went out, not because somebody ticked.
 */
export function issueRevision(
  projectId: string,
  deliverableId: string,
  input: { revision_code: string; notes?: string | null },
): Promise<readonly Deliverable[]> {
  return deliverableCall(`${dwgPath(projectId)}/${encodeURIComponent(deliverableId)}/revisions`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function moveDeliverable(
  projectId: string,
  deliverableId: string,
  input: { status: 'planned' | 'in_progress' | 'cancelled'; reason?: string },
): Promise<readonly Deliverable[]> {
  return deliverableCall(`${dwgPath(projectId)}/${encodeURIComponent(deliverableId)}/status`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function deleteDeliverable(
  projectId: string,
  deliverableId: string,
): Promise<readonly Deliverable[]> {
  return deliverableCall(`${dwgPath(projectId)}/${encodeURIComponent(deliverableId)}`, {
    method: 'DELETE',
  });
}

// ------------------------------------------------------------------ submittals

export type SubmittalStatus =
  | 'draft'
  | 'internal_review'
  | 'submitted'
  | 'approved'
  | 'approved_with_comments'
  | 'rejected'
  | 'resubmit_required'
  | 'void';

export type SlaStatus = 'not_sent' | 'in_time' | 'due_soon' | 'overdue' | 'red' | 'closed';

export interface ApprovalAction {
  readonly id: string;
  readonly action: string;
  readonly actor_name: string | null;
  readonly revision_code: string | null;
  readonly comment: string | null;
  readonly occurred_at: string;
}

export interface Submittal {
  readonly id: string;
  readonly number: string;
  readonly title: string;
  readonly status: SubmittalStatus;
  readonly outcome: string | null;
  readonly deliverable_id: string | null;
  readonly deliverable_number: string | null;
  readonly revision_id: string | null;
  readonly revision_code: string | null;
  readonly recipient_name: string | null;
  readonly recipient_email: string | null;
  readonly required_response_date: string | null;
  readonly submitted_at: string | null;
  readonly responded_at: string | null;
  readonly void_reason: string | null;
  readonly days_overdue: number | null;
  readonly sla: SlaStatus;
  /** How many times it has been round. Two or more tells a story. */
  readonly round: number;
  readonly allowed_next: readonly SubmittalStatus[];
  /** The record nobody can edit. Oldest first. */
  readonly history: readonly ApprovalAction[];
}

async function submittalCall(path: string, init?: RequestInit): Promise<readonly Submittal[]> {
  const body = await request<{ data: readonly Submittal[] }>(path, init ?? {});
  return body.data;
}

const subPath = (projectId: string): string =>
  `/v1/projects/${encodeURIComponent(projectId)}/submittals`;

/**
 * The register, which the server bounds. A subcontractor's total counts only
 * what they may see — see `api/paging.ts`.
 */
export function listSubmittals(
  projectId: string,
  limit?: number,
): Promise<RegisterPage<Submittal>> {
  return pageCall<Submittal>(subPath(projectId), limit);
}

export function createSubmittal(
  projectId: string,
  input: {
    title: string;
    deliverable_id?: string | null;
    revision_id?: string | null;
    recipient_name?: string | null;
    required_response_date?: string | null;
  },
): Promise<readonly Submittal[]> {
  return submittalCall(subPath(projectId), { method: 'POST', body: JSON.stringify(input) });
}

export function updateSubmittal(
  projectId: string,
  submittalId: string,
  input: {
    title?: string;
    deliverable_id?: string | null;
    revision_id?: string | null;
    recipient_name?: string | null;
    required_response_date?: string | null;
  },
): Promise<readonly Submittal[]> {
  return submittalCall(`${subPath(projectId)}/${encodeURIComponent(submittalId)}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/**
 * Move a submittal — including deciding it.
 *
 * Guarded by `submittal:approve` on the server, not `edit`. A Design
 * Coordinator cannot approve their own work by calling this directly.
 */
export function moveSubmittal(
  projectId: string,
  submittalId: string,
  input: {
    status: SubmittalStatus;
    reason?: string;
    comment?: string | null;
    required_response_date?: string | null;
  },
): Promise<readonly Submittal[]> {
  return submittalCall(`${subPath(projectId)}/${encodeURIComponent(submittalId)}/status`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function deleteSubmittal(
  projectId: string,
  submittalId: string,
): Promise<readonly Submittal[]> {
  return submittalCall(`${subPath(projectId)}/${encodeURIComponent(submittalId)}`, {
    method: 'DELETE',
  });
}
